import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BellRing, X } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useTenant } from "../contexts/TenantContext";
import { inr } from "../lib/api";
import { startAlertLoop } from "../lib/alertSound";
import { onAppStateChange } from "../lib/capacitor";
import {
    ORDER_ALERT_EVENT, PUSH_OPENED_EVENT, ackOrderAlert, addAlert, alertFromPush, alertSoundUrl, createAckStore,
    removeAlert, userWantsPersistentAlerts,
} from "../lib/orderAlert";

const HEADINGS = { new_order: "New order", new_ride: "New ride available", cancelled: "Order cancelled" };
const CANCELLED_BY = {
    CUSTOMER: "Cancelled by the customer", RESTAURANT_OWNER: "Cancelled by the shop",
    TENANT_ADMIN: "Cancelled by admin", RIDER: "Cancelled by the rider",
};

const isNativeShell = () => document.documentElement.classList.contains("capacitor-platform");

/**
 * Shows persistent order alerts (orderAlert.js) one at a time and loops the tenant's alert sound until the
 * user acknowledges them or the loop's time limit runs out. The dialog itself stays until acknowledged.
 */
export default function OrderAlertHost() {
    const nav = useNavigate();
    const { tenant } = useTenant() || {};
    const { user } = useAuth() || {};
    const [queue, setQueue] = useState([]);
    const acks = useRef(null);
    if (!acks.current) acks.current = createAckStore();
    const stopSound = useRef(null);
    const soundUrl = useRef(null);
    // The native app keeps running JS in the background (the Live Orders / rider home refresh too), so an
    // alert can be raised there. It never rings in the background — the OS notification covers that.
    const inBackground = useRef(false);
    soundUrl.current = alertSoundUrl(tenant?.new_order_alert_config);
    // This account's own on/off switch (admin sidebar). Pushes and the feed are already filtered by the server;
    // this only closes what's on screen when the switch is turned off on this device.
    const allowed = userWantsPersistentAlerts(user);

    useEffect(() => {
        const add = (alert) => alert && setQueue((q) => addAlert(q, alert, acks.current.has));
        const onPush = (e) => { if (e.detail?.persistentAlert) add(alertFromPush(e.detail)); };
        const onRaised = (e) => add(e.detail);
        // Tapping the OS notification opens that order, which acknowledges its alert.
        const onOpened = (e) => {
            const key = alertFromPush(e.detail)?.key;
            if (!key) return;
            acks.current.add(key);
            ackOrderAlert(key);                      // tapping the notification also stops the re-sends
            setQueue((q) => removeAlert(q, key));
        };
        window.addEventListener("bizil:push", onPush);
        window.addEventListener(ORDER_ALERT_EVENT, onRaised);
        window.addEventListener(PUSH_OPENED_EVENT, onOpened);
        return () => {
            window.removeEventListener("bizil:push", onPush);
            window.removeEventListener(ORDER_ALERT_EVENT, onRaised);
            window.removeEventListener(PUSH_OPENED_EVENT, onOpened);
        };
    }, []);

    // Switched off while an alert is showing: close it and silence it straight away.
    useEffect(() => {
        if (!allowed) setQueue([]);
    }, [allowed]);

    // Each genuinely new alert (re)starts the loop; duplicates never reach the queue, so they never restart it.
    // "Sound: off" shows the dialog silently, and "repeat: off" plays the alert once instead of looping — both
    // decided per event by the tenant's notification rules and carried on the alert itself.
    const newest = queue.length ? queue[queue.length - 1] : null;
    const newestKey = newest?.key || null;
    const wantsSound = newest ? newest.sound !== false : false;
    const wantsRepeat = newest ? newest.repeat !== false : false;
    useEffect(() => {
        stopSound.current?.();
        stopSound.current = newestKey && wantsSound && !inBackground.current
            ? startAlertLoop({ url: soundUrl.current, repeat: wantsRepeat })
            : null;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [newestKey]);

    useEffect(() => {
        // In the native app, leaving it silences the loop (the OS notification is already in the tray);
        // the dialog is still there to acknowledge on return. A browser tab in the background keeps ringing.
        // Android's WebView doesn't reliably report itself hidden, so the app-state event is the main signal.
        const setBackground = (hidden) => {
            if (!isNativeShell()) return;
            inBackground.current = hidden;
            if (hidden) { stopSound.current?.(); stopSound.current = null; }
        };
        const onVisibility = () => { if (document.hidden) setBackground(true); };
        document.addEventListener("visibilitychange", onVisibility);
        let unsubscribe = () => {};
        let unmounted = false;
        onAppStateChange((active) => setBackground(!active)).then((off) => {
            if (unmounted) off(); else unsubscribe = off;
        });
        return () => {
            unmounted = true;
            unsubscribe();
            document.removeEventListener("visibilitychange", onVisibility);
            stopSound.current?.();
        };
    }, []);

    const current = queue[0];
    if (!current) return null;

    const acknowledge = (open) => {
        acks.current.add(current.key);
        ackOrderAlert(current.key);                  // stops the server re-sending this one to this account

        if (queue.length === 1) { stopSound.current?.(); stopSound.current = null; }
        setQueue((q) => removeAlert(q, current.key));
        if (open && current.path) nav(current.path);
    };

    const cancelled = current.kind === "cancelled";
    const details = [
        current.customerName,
        current.amount !== "" && !Number.isNaN(Number(current.amount)) ? inr(current.amount) : "",
        current.itemsCount ? `${current.itemsCount} item${String(current.itemsCount) === "1" ? "" : "s"}` : "",
    ].filter(Boolean).join(" · ");

    return (
        <div className="fixed inset-0 z-[10000] bg-black/60 flex items-center justify-center p-4"
            role="alertdialog" aria-modal="true" aria-labelledby="order-alert-heading" data-testid="order-alert">
            <div className="w-full max-w-sm bg-card text-foreground rounded-2xl shadow-2xl overflow-hidden">
                <div className={`flex items-center gap-3 px-4 py-3 text-white ${cancelled ? "bg-red-600" : "bg-amber-500"}`}>
                    <BellRing size={22} className="animate-pulse shrink-0" />
                    <p id="order-alert-heading" className="flex-1 font-extrabold uppercase tracking-wide" data-testid="order-alert-heading">
                        {HEADINGS[current.kind]}
                    </p>
                    <button type="button" aria-label="Dismiss alert" data-testid="order-alert-close"
                        onClick={() => acknowledge(false)}
                        className="touch-target inline-flex items-center justify-center rounded-lg hover:bg-white/15">
                        <X size={20} />
                    </button>
                </div>
                <div className="px-4 py-4 space-y-1.5">
                    <p className="text-xl font-bold" data-testid="order-alert-number">#{current.orderNumber}</p>
                    {details && <p className="text-sm font-semibold">{details}</p>}
                    {cancelled ? (
                        <>
                            <p className="text-sm text-muted-foreground">{CANCELLED_BY[current.cancelledBy] || "This order was cancelled"}</p>
                            {current.cancelReason && <p className="text-sm text-muted-foreground">Reason: {current.cancelReason}</p>}
                        </>
                    ) : (
                        current.body && <p className="text-sm text-muted-foreground">{current.body}</p>
                    )}
                    {queue.length > 1 && (
                        <p className="text-xs font-semibold text-primary" data-testid="order-alert-more">
                            {queue.length - 1} more alert{queue.length > 2 ? "s" : ""} waiting
                        </p>
                    )}
                </div>
                <div className="px-4 pb-4 grid grid-cols-2 gap-2">
                    <button type="button" data-testid="order-alert-ok" onClick={() => acknowledge(false)}
                        className="touch-target rounded-xl border border-border font-bold py-3 hover:bg-muted">
                        OK
                    </button>
                    <button type="button" data-testid="order-alert-view" disabled={!current.path}
                        onClick={() => acknowledge(true)}
                        className="touch-target rounded-xl bg-primary text-primary-foreground font-bold py-3 disabled:opacity-50">
                        {current.kind === "new_ride" ? "View rides" : "View order"}
                    </button>
                </div>
            </div>
        </div>
    );
}
